// ============================================================================
// INLINE VISUAL DIRECTIVES
// ============================================================================
// The chat agent embeds visuals in its reply text rather than in a side channel:
//   [[CHART: {json}]]   -> a recharts chart, or a Saudi-region choropleth
//   ```svg ... ```      -> a sanitized vector figure
//   ```mermaid ... ```  -> a mermaid diagram
//
// This module owns the parsing so BOTH paths that reconstruct a message use it:
// the live webhook reply in Chat.js, and messages replayed from server history
// in AppContext.js. When only the live path parsed directives, reopening a
// conversation showed the raw [[CHART: {...}]] JSON as chat text and lost the
// visual — so this is deliberately shared, not duplicated.
// ============================================================================

import { resolveRegionId, regions as SA_REGIONS } from './saudiRegions';

// ---------------------------------------------------------------------------
// Chart data normalisation
//
// The upstream agent emits Chart.js-shaped payloads ({labels, datasets}) that
// are sometimes structurally broken: the same category repeated across rows
// (one recharts category slot per occurrence -> "RIYADH" drawn twice with the
// series split across separate bars), missing (category, series) combinations,
// and two datasets sharing a `label`. We normalise defensively here and shout
// about it in the console so the payload bug stays visible.
// ---------------------------------------------------------------------------

// Numbers only; anything non-numeric (null, '', 'N/A') becomes null so recharts
// draws a GAP rather than a phantom zero-height bar / a line dropping to 0.
export function toNumberOrNull(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return null;
    // "1,524,434" is how a model most often writes a figure it just read in
    // prose. Number() gives NaN for it, which used to drop the value silently.
    // Only strip separators when what remains is a bare number — never touch a
    // string that carries units or words, which should still be rejected.
    const bare = s.replace(/[,٬ \s]/g, '');
    if (/^[+-]?\d+(\.\d+)?$/.test(bare)) return Number(bare);
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Row objects are keyed by series name, so duplicate names would silently
// overwrite each other. Suffix collisions instead.
function uniquifySeriesNames(rawNames) {
  const used = new Set();
  return rawNames.map((raw, i) => {
    const base = raw === null || raw === undefined || String(raw).trim() === ''
      ? `Series ${i + 1}`
      : String(raw);
    if (!used.has(base)) { used.add(base); return base; }
    let n = 2;
    while (used.has(`${base} (${n})`)) n += 1;
    const name = `${base} (${n})`;
    used.add(name);
    return name;
  });
}

const LONG_CATEGORY_KEYS = ['category', 'label', 'name', 'x', 'group'];
const LONG_SERIES_KEYS = ['series', 'dataset', 'measure', 'metric', 'key'];
const LONG_VALUE_KEYS = ['value', 'y', 'count', 'val'];
function pickKey(obj, keys) {
  for (const k of keys) if (obj[k] !== undefined) return obj[k];
  return undefined;
}

// Long/tidy format — {format:'long', rows:[{category, series, value}]} — is the
// sanctioned way for the model to express exactly what the SQL returned. Pivot
// it to the wide {labels, datasets} shape client-side.
function pivotLong(rows) {
  const labels = [];
  const seenLabels = new Set();
  const seriesOrder = [];
  const seenSeries = new Set();
  const cells = new Map(); // `${series}\u0000${label}` -> value
  rows.forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const label = String(pickKey(r, LONG_CATEGORY_KEYS) ?? '');
    const rawSeries = pickKey(r, LONG_SERIES_KEYS);
    const series = rawSeries === undefined || rawSeries === null || String(rawSeries) === ''
      ? 'Value'
      : String(rawSeries);
    const value = toNumberOrNull(pickKey(r, LONG_VALUE_KEYS));
    if (!seenLabels.has(label)) { seenLabels.add(label); labels.push(label); }
    if (!seenSeries.has(series)) { seenSeries.add(series); seriesOrder.push(series); }
    const cellKey = `${series}\u0000${label}`;
    // First non-null wins — same collision rule as the wide path.
    if (cells.get(cellKey) === undefined || cells.get(cellKey) === null) cells.set(cellKey, value);
  });
  return {
    labels,
    datasets: seriesOrder.map((s) => ({
      label: s,
      data: labels.map((l) => {
        const v = cells.get(`${s}\u0000${l}`);
        return v === undefined ? null : v; // absent combination -> gap
      }),
    })),
  };
}

// Unwrap the payload: `data` may nest the real chart spec.
function chartSource(block) {
  const b = block || {};
  const d = b.data;
  if (d && typeof d === 'object' && (d.labels || d.datasets || d.rows || d.format === 'long')) return d;
  return b;
}

// Normalise any accepted payload into the wide {labels, datasets} shape.
function toWide(block) {
  const src = chartSource(block);
  const isLong = String(src.format || '').toLowerCase() === 'long'
    || (Array.isArray(src.rows) && !Array.isArray(src.labels) && !Array.isArray(src.datasets));
  if (isLong) return pivotLong(Array.isArray(src.rows) ? src.rows : []);
  return {
    labels: Array.isArray(src.labels) ? src.labels : [],
    datasets: Array.isArray(src.datasets) ? src.datasets : [],
  };
}

// Convert to the row-per-label array recharts expects. Duplicate labels are
// collapsed onto one row (recharts then groups the multiple <Bar> elements
// side-by-side automatically — no extra grouping config needed).
export function toRows(block) {
  const { labels, datasets } = toWide(block);
  const seriesNames = uniquifySeriesNames(datasets.map((d) => (d && d.label)));

  const byLabel = new Map();
  const rows = [];
  const duplicateLabels = [];
  labels.forEach((label, i) => {
    const name = String(label);
    let row = byLabel.get(name);
    if (!row) {
      row = { name };
      byLabel.set(name, row);
      rows.push(row);
    } else if (!duplicateLabels.includes(name)) {
      duplicateLabels.push(name);
    }
    datasets.forEach((d, di) => {
      const key = seriesNames[di];
      const value = toNumberOrNull(Array.isArray(d && d.data) ? d.data[i] : undefined);
      // On collision keep the FIRST non-null value. Summing would be right for
      // counts but silently wrong for averages and rates — a wrong number is
      // worse than an imperfect chart.
      if (row[key] === undefined || row[key] === null) row[key] = value;
    });
  });
  // Every row carries every series key; absent combinations stay null (a gap).
  rows.forEach((row) => seriesNames.forEach((s) => { if (row[s] === undefined) row[s] = null; }));

  if (duplicateLabels.length) {
    console.warn(
      '⚠️ Chart payload has duplicate category labels — collapsed to one row each ' +
      '(first non-null value kept per series). Fix the upstream payload:',
      duplicateLabels, block,
    );
  }
  return { rows, seriesNames, duplicateLabels };
}

// Parse-time structural validation, used before a chart is ever built. A
// misleading chart in a ministry demo is worse than a plain table, so callers
// prefer a data table when this returns issues.
export function validateChartSpec(spec) {
  const issues = [];
  if (!spec || typeof spec !== 'object') return { valid: false, issues: ['spec is not an object'] };
  const src = chartSource(spec);
  const isLong = String(src.format || '').toLowerCase() === 'long'
    || (Array.isArray(src.rows) && !Array.isArray(src.labels) && !Array.isArray(src.datasets));
  if (isLong) {
    // Long format is pivoted client-side, so duplicate rows are expected and safe.
    if (!Array.isArray(src.rows) || src.rows.length === 0) issues.push('long-format payload has no rows');
    return { valid: issues.length === 0, issues, format: 'long' };
  }
  const labels = Array.isArray(src.labels) ? src.labels : [];
  const datasets = Array.isArray(src.datasets) ? src.datasets : [];
  if (!labels.length) issues.push('no labels');
  if (!datasets.length) issues.push('no datasets');
  const seen = new Set();
  const dupes = [];
  labels.forEach((l) => {
    const k = String(l);
    if (seen.has(k)) { if (!dupes.includes(k)) dupes.push(k); } else seen.add(k);
  });
  if (dupes.length) issues.push(`duplicate labels: ${dupes.join(', ')}`);
  datasets.forEach((d, i) => {
    const len = Array.isArray(d && d.data) ? d.data.length : -1;
    if (len === -1) issues.push(`dataset ${i} ("${(d && d.label) || i}") has no data array`);
    else if (labels.length && len !== labels.length) {
      issues.push(`dataset ${i} ("${(d && d.label) || i}") has ${len} values for ${labels.length} labels`);
    }
  });
  return { valid: issues.length === 0, issues, format: 'wide' };
}

// Fallback renderer input: a GFM markdown table of exactly the numbers we were
// given, for charts we know are structurally wrong.
export function chartSpecToMarkdownTable(spec) {
  const esc0 = (v) => String(v).replace(/\|/g, '\\|');

  // A map spec carries `regions`, not labels/datasets, so toWide() would return
  // nothing and the caller would render an empty message. Give it its own
  // two-column table — a map that cannot be drawn should still show its numbers.
  if (spec && Array.isArray(spec.regions) && spec.regions.length) {
    const rows = spec.regions
      .filter((r) => r && typeof r === 'object')
      .map((r) => {
        const n = toNumberOrNull(r.value);
        return `| ${esc0(r.region ?? r.id ?? '—')} | ${n === null ? '—' : String(n)} |`;
      });
    if (rows.length) {
      const head = `| ${esc0(spec.x_label || 'Region')} | ${esc0(spec.value_label || spec.valueLabel || 'Value')} |`;
      const t = spec.title ? `**${esc0(spec.title)}**\n\n` : '';
      return `${t}${[head, '| --- | --- |', ...rows].join('\n')}`;
    }
  }

  const { labels, datasets } = toWide(spec);
  if (!labels.length || !datasets.length) return '';
  const names = uniquifySeriesNames(datasets.map((d) => (d && d.label)));
  const esc = esc0;
  const cell = (v) => {
    const n = toNumberOrNull(v);
    return n === null ? '—' : String(n);
  };
  const head = `| ${esc(spec.x_label || 'Category')} | ${names.map(esc).join(' | ')} |`;
  const sep = `| --- | ${names.map(() => '---').join(' | ')} |`;
  const body = labels.map((label, i) => (
    `| ${esc(label)} | ${datasets.map((d) => cell(Array.isArray(d && d.data) ? d.data[i] : undefined)).join(' | ')} |`
  ));
  const title = spec.title ? `**${esc(spec.title)}**\n\n` : '';
  return `${title}${[head, sep, ...body].join('\n')}`;
}

// ---------------------------------------------------------------------------
// Cartesian chart -> region choropleth
//
// The generator is asked to pick chartType "map" for Saudi-region data, but it
// is a small model reading a long prompt and it complies only sometimes — the
// same question phrased three ways produced map, bar, and bar. Whether a set of
// categories IS the Saudi regions is a fact about the data, not a judgement, so
// we decide it here instead: any single-series chart whose labels all resolve to
// distinct regions is rendered as the map it should have been.
//
// Deliberately strict — it fails closed to the original chart when unsure:
//   * one dataset only (a choropleth cannot show boys-vs-girls per region)
//   * at least 3 regions (below that a map communicates nothing a table doesn't)
//   * EVERY label must resolve, to a DISTINCT region (a chart mixing regions
//     with cities, or with a "Total" row, stays a bar chart)
//   * every value must be a real number
// ---------------------------------------------------------------------------
const MIN_REGIONS_FOR_MAP = 3;

export function cartesianToRegionMap(spec) {
  if (!spec || typeof spec !== 'object') return null;
  const labels = spec.labels;
  const datasets = spec.datasets;
  if (!Array.isArray(labels) || labels.length < MIN_REGIONS_FOR_MAP) return null;
  if (!Array.isArray(datasets) || datasets.length !== 1) return null;

  const series = datasets[0];
  const data = series && series.data;
  if (!Array.isArray(data) || data.length !== labels.length) return null;

  const ids = labels.map(resolveRegionId);
  if (ids.some((id) => !id)) return null;
  if (new Set(ids).size !== ids.length) return null;
  if (ids.length > SA_REGIONS.length) return null;

  const regions = [];
  for (let i = 0; i < ids.length; i += 1) {
    const v = data[i];
    // null/undefined/'' must be rejected explicitly: Number(null) is 0, and a
    // gap silently painted as zero is the one failure a map cannot survive.
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return null;
    const n = typeof v === 'string' ? Number(v.replace(/[,٬\s]/g, '')) : Number(v);
    if (!Number.isFinite(n)) return null;
    regions.push({ region: ids[i], value: n });
  }

  return {
    chartType: 'map',
    title: spec.title,
    // The colour legend needs the measure name ("عدد الطلاب" / "Students"),
    // which on a cartesian spec lives on the series or the y axis.
    value_label: series.label || spec.y_label || spec.value_label || 'Value',
    regions,
  };
}

// svg/mermaid use fenced blocks because their syntax can contain ]] but never
// ``` — so the closing fence is a collision-proof delimiter.
// Built fresh per call: a /g/ regex carries lastIndex, which must not be shared.
const buildVisualRe = () =>
  /\[\[\s*CHART\s*:\s*([\s\S]+?)\]\]|```\s*(svg|mermaid)[^\n]*\n([\s\S]*?)```|\[\[\s*SOURCES\s*:\s*([\s\S]+?)\]\]/gi;

const failed = (msg) => ({ type: 'text', markdown: `_${msg}_` });

// The pipeline delivers pretty-printed directives with the newlines still
// escaped, which JSON.parse rejects; retry unescaped before giving up.
function parseDirectiveJson(raw) {
  const s = String(raw || '').trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  try {
    return JSON.parse(s);
  } catch (e) {
    try {
      return JSON.parse(s.replace(/\\n/g, '\n').replace(/\\t/g, '\t'));
    } catch (e2) {
      return null;
    }
  }
}

/**
 * Parse a [[SOURCES: {"items":[{title,url,snippet}]}]] directive.
 *
 * The payload is an OBJECT wrapper on purpose. A bare JSON array would end with
 * "]" immediately before the "]]" terminator, so the non-greedy matcher would
 * stop at the wrong bracket and hand us invalid JSON. An array is still accepted
 * if one somehow arrives, since being lenient here costs nothing.
 */
export function parseSourcesDirective(raw) {
  const parsed = parseDirectiveJson(raw);
  if (!parsed) {
    console.warn('⚠️ Could not parse SOURCES directive:', raw);
    return null;
  }
  const items = Array.isArray(parsed) ? parsed : parsed.items;
  if (!Array.isArray(items) || !items.length) return null;
  // Keep only entries that name a link; SourcesBlock validates the URL itself.
  const usable = items.filter((it) => it && typeof it === 'object' && it.url);
  if (!usable.length) return null;
  return { type: 'sources', title: parsed.title, items: usable };
}

/**
 * Parse one [[CHART: ...]] payload into a block spec.
 *
 * Never returns null: the directive is always stripped from the visible text, so
 * a null would delete the content outright. A payload that cannot be charted
 * degrades to a data table carrying exactly the numbers we were given — a chart
 * known to be wrong is more misleading than a table.
 */
export function parseChartDirective(raw) {
  if (!raw) return null;
  const s = raw.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  let spec = null;
  try {
    const parsed = JSON.parse(s);
    if (parsed && typeof parsed === 'object') spec = parsed;
  } catch (e) {
    // A pretty-printed directive reaches us with the newlines still escaped
    // (this pipeline delivers literal backslash-n), which JSON.parse rejects.
    // Unescape and try once more before giving up.
    try {
      const parsed = JSON.parse(s.replace(/\\n/g, '\n').replace(/\\t/g, '\t'));
      if (parsed && typeof parsed === 'object') spec = parsed;
    } catch (e2) {
      console.warn('⚠️ Could not parse CHART directive:', e, raw);
    }
  }
  if (!spec) return failed('A chart could not be rendered.');

  // Region choropleth ("map"/"choropleth") takes an EARLY path: a map has no
  // labels/datasets, so it must never reach validateChartSpec (which is for
  // cartesian charts and would reject it). Validate minimally: a non-empty
  // `regions` array of {region, value}. If that fails, fall back to a table.
  const chartKind = String(spec.chartType || spec.chart_type || spec.type || '').toLowerCase();
  if (chartKind === 'map' || chartKind === 'choropleth') {
    const usable = (Array.isArray(spec.regions) ? spec.regions : [])
      .filter((r) => r && (r.region !== undefined || r.id !== undefined) && r.value !== undefined);
    // `type` last: a stray `type` key inside the payload must not decide the
    // block kind, or the map is routed to ChartBlock and renders nothing.
    if (usable.length) return { ...spec, type: 'map' };
    console.warn('⚠️ MAP directive has no usable regions — rendering a data table instead:', spec);
    const mapTable = chartSpecToMarkdownTable(spec);
    return mapTable
      ? { type: 'text', markdown: mapTable }
      : failed('A regional map could not be rendered.');
  }

  const { valid, issues } = validateChartSpec(spec);
  if (valid) {
    // Regions asked for as a bar/pie chart are still regions — draw the map.
    const asMap = cartesianToRegionMap(spec);
    if (asMap) return { ...asMap, type: 'map' };
    return { ...spec, type: 'chart' };
  }

  console.warn('⚠️ CHART directive is structurally invalid — rendering a data table instead:', issues, spec);
  const table = chartSpecToMarkdownTable(spec);
  return table ? { type: 'text', markdown: table } : failed('A chart could not be rendered.');
}

/**
 * Split a reply into its visible prose and its visual blocks, in document order.
 * Directives are always stripped from the text, whether or not they parsed.
 *
 * @returns {{ text: string, blocks: Array<object> }}
 */
export function splitVisualDirectives(content) {
  const source = typeof content === 'string' ? content : '';
  if (!source) return { text: '', blocks: [] };

  const visualRe = buildVisualRe();
  const blocks = [];
  let m;
  while ((m = visualRe.exec(source)) !== null) {
    if (m[1] != null) {
      const block = parseChartDirective(m[1]);
      if (block) blocks.push(block);
    } else if (m[4] != null) {
      const block = parseSourcesDirective(m[4]);
      if (block) blocks.push(block);
    } else if (m[3] && m[3].trim()) {
      const lang = (m[2] || '').toLowerCase();
      const body = m[3].trim();
      blocks.push(lang === 'svg'
        ? { type: 'figure', svg: body }
        : { type: 'diagram', format: 'mermaid', content: body });
    }
  }

  return { text: source.replace(buildVisualRe(), '').trim(), blocks };
}
